import NurturingHistory, {
  CreateShortUrlRequest,
  attributionHistoryHintsFromRow,
} from 'app/apiService/nurturingHistory/nurturingHistoryApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';

export interface NurturingHistoryParams {
  emailAddress?: string;
  opportunityId: string;
  dealerId: string;
  rowDetails?: any;
  analytics?: boolean;
  /** When navigating from Measure page, pass so we can restore it when user comes back (keeps query cache keys matching) */
  measurePageState?: {
    timeperiodSelect: string;
    start_date?: string;
    end_date?: string;
  };
}

export const handleNurturingHistoryRedirect = async (
  params: NurturingHistoryParams,
  dispatch: any,
  history: any,
  currentBreadcrumbs?: any[],
): Promise<void> => {
  try {
    // Extract email from rowDetails if not provided directly
    const emailAddress = params.emailAddress ||
      params.rowDetails?.email_address ||
      params.rowDetails?.emailAddress ||
      params.rowDetails?.email ||
      params.rowDetails?.Email_mailshake ||
      '';

    if (!emailAddress) {
      throw new Error('Email address is required for nurturing history');
    }

    if (!params.opportunityId) {
      throw new Error('Opportunity ID is required for nurturing history');
    }

    if (!params.dealerId) {
      throw new Error('Dealer ID is required for nurturing history');
    }

    // Prepare payload for API call
    const payload: CreateShortUrlRequest = {
      emailAddress,
      opportunityId: params.opportunityId,
      dealerId: params.dealerId,
      analytics: params.analytics,
      // Pass through originating_dealer_id from the clicked row when available
      originating_dealer_id:
        params.rowDetails?.originating_dealer_id ??
        params.rowDetails?.dealer_id ??
        undefined,
      ...attributionHistoryHintsFromRow(params.rowDetails),
    };

    console.log('Creating short URL with payload:', payload);

    // Call API to create short URL
    const response = await NurturingHistory.createShortUrl(payload);

    if (response.statusCode !== 201 && response.statusCode !== 200) {
      throw new Error(response.message || 'Failed to create short URL');
    }

    const shortUrl = response.body?.shortUrl;

    if (!shortUrl) {
      throw new Error('Short URL not received from API response');
    }

    console.log('Short URL created:', shortUrl);

    // Extract URL ID from the short URL
    const urlId = NurturingHistory.extractUrlId(shortUrl);

    if (!urlId) {
      throw new Error('Could not extract URL ID from short URL');
    }

    console.log('Extracted URL ID:', urlId);

    // Prepare breadcrumbs for the nurturing history page
    const nurturingHistoryBreadcrumbs = [
      ...(currentBreadcrumbs || []).map(breadcrumb => ({ ...breadcrumb, isActive: false })),
      {
        label: `Nurturing History (${emailAddress})`,
        link: `/url/${urlId}`,
        isActive: true
      }
    ];

    // Navigate to nurturing history: use history.push so we stay in same SPA and React Query cache is preserved when coming back
    const targetUrl = `/url/${urlId}`;
    const currentPath = window.location.pathname;
    const isInAIAvatarLayout = currentPath.includes('/ai-avatar-layout');

    const navigationState = {
      breadcrumbsList: nurturingHistoryBreadcrumbs,
      fromPage: 'AutopilotPage',
      fromAIAvatarLayout: isInAIAvatarLayout,
      emailAddress,
      opportunityId: params.opportunityId,
      dealerId: params.dealerId,
      ...(params.measurePageState && { measurePageState: params.measurePageState }),
    };

    // Always use history.push so back-navigation reuses query cache (window.location.href would reload app and clear cache)
    history.push(targetUrl, navigationState);

  } catch (error) {
    console.error('Error creating nurturing history link:', error);

    // Show error notification
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to create nurturing history link',
      }),
    );
  }
};

export default {
  handleNurturingHistoryRedirect,
}; 